import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type Paged } from '../engine/api/client';
import { useApi } from '../engine/api/context';
import { useToast } from '../engine/design/primitives/Toast';
import { ME_KEY } from './PortalContext';

// Portal lists take plain query params (status=, q=, mine=, unpaid=…, limit=, cursor=) and return
// { data, next_cursor } — see the list overrides in src/modules/portal/resources.ts. This is the
// engine's react-query pattern (useResourceList) over the portal client's request(); the query
// keys are namespaced under ['portal', …] so the shell's `me` and the pages invalidate together.

export type Params = Record<string, string | number | boolean | null | undefined>;

export const portalKeys = {
  list: (resource: string, params: Params) => ['portal', 'list', resource, params] as const,
  item: (resource: string, id: string) => ['portal', 'item', resource, id] as const,
  aggregate: (name: string, ...rest: unknown[]) => ['portal', name, ...rest] as const,
};

export function usePortalList<T>(resource: string, params: Params = {}, opts: { limit?: number; enabled?: boolean; staleTime?: number } = {}) {
  const api = useApi();
  const limit = opts.limit ?? 25;
  const q = useInfiniteQuery({
    queryKey: portalKeys.list(resource, { ...params, limit }),
    queryFn: ({ pageParam }) => api.request<Paged<T>>(`/v1/${resource}`, { query: { ...params, limit, cursor: (pageParam as string | null) ?? undefined } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Paged<T>) => last.next_cursor ?? null,
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
  });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.data) ?? [], [q.data]);
  return {
    rows,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error as ApiError | null,
    hasMore: q.hasNextPage ?? false,
    loadingMore: q.isFetchingNextPage,
    loadMore: () => void q.fetchNextPage(),
    refetch: () => void q.refetch(),
  };
}

export function usePortalItem<T>(resource: string, id: string | null | undefined, opts: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery<T, ApiError>({
    queryKey: portalKeys.item(resource, id ?? ''),
    queryFn: () => api.request<T>(`/v1/${resource}/${encodeURIComponent(id!)}`),
    enabled: Boolean(id) && (opts.enabled ?? true),
    retry: false,
  });
}

/** A GET aggregate under /portal/v1 (types, inbox, roles…). */
export function usePortalGet<T>(path: string, opts: { enabled?: boolean; staleTime?: number; query?: Params } = {}) {
  const api = useApi();
  return useQuery<T, ApiError>({
    queryKey: portalKeys.aggregate(path, opts.query ?? {}),
    queryFn: () => api.request<T>(`/v1${path}`, { query: opts.query }),
    enabled: opts.enabled ?? true,
    staleTime: opts.staleTime,
    retry: false,
  });
}

/** Invalidate a resource's lists/items plus the shell's counts. */
export function usePortalInvalidate() {
  const qc = useQueryClient();
  return (resource?: string, id?: string) => {
    if (resource) {
      void qc.invalidateQueries({ queryKey: ['portal', 'list', resource] });
      if (id) void qc.invalidateQueries({ queryKey: portalKeys.item(resource, id) });
      else void qc.invalidateQueries({ queryKey: ['portal', 'item', resource] });
    }
    void qc.invalidateQueries({ queryKey: ME_KEY });
    void qc.invalidateQueries({ queryKey: ['portal', 'aggregate'] });
  };
}

/** A mutation that toasts failures (Problem detail) and invalidates on success. */
export function usePortalMutation<R, V = void>(fn: (vars: V) => Promise<R>, opts: { invalidate?: [string, string?] | (() => void); success?: string | ((r: R) => string); onSuccess?: (r: R, v: V) => void; onError?: (e: ApiError) => void } = {}) {
  const toast = useToast();
  const invalidate = usePortalInvalidate();
  return useMutation<R, ApiError, V>({
    mutationFn: fn,
    onSuccess: (r, v) => {
      if (typeof opts.invalidate === 'function') opts.invalidate();
      else if (opts.invalidate) invalidate(opts.invalidate[0], opts.invalidate[1]);
      else invalidate();
      if (opts.success) toast({ title: typeof opts.success === 'function' ? opts.success(r) : opts.success });
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (opts.onError) return opts.onError(e);
      toast({ kind: 'error', title: e instanceof ApiError ? e.detail : String(e) });
    },
  });
}
